import { useMemo, useState } from "react";
import { Paper, Stack, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import ModuleInvoicePanel from "components/ModuleInvoice/ModuleInvoicePanel";
import ModulePaymentActionModal from "components/Modals/ModulePaymentActionModal";
import PendingModulePaymentDialog from "components/Payments/PendingModulePaymentDialog";
import { P } from "const/permissions";
import { getApiError } from "utils/apiError";
import { hasPermission } from "utils/permissionUtils";
import ChargesSummaryCard from "./ChargesSummaryCard";
import PaymentRow from "./PaymentRow";
import RecordPaymentForm from "./RecordPaymentForm";
import { invalidateModuleFinance } from "./moduleFinanceQueries";
import { addModulePayment, resubmitModulePayment, useModulePayments } from "./modulePaymentsApi";
import { PRIMARY_CHARGE_LABELS, requiresPaymentMethod } from "./modulePaymentsConstants";
import {
  balanceDue,
  chargeSummary,
  refundableAvailable,
  totalVerifiedPaid,
} from "./modulePaymentsLogic";

const CLOSED = { open: false, mode: null, action: null, payment: null };

const ModulePaymentsTab = ({ moduleType, moduleId, clientId, charges, onPaymentSubmitted }) => {
  const queryClient = useQueryClient();
  const { data: payments = [], isLoading } = useModulePayments(moduleType, moduleId);
  const [formSeed, setFormSeed] = useState({ nonce: 0, draft: null, attachments: [] });
  const [actionState, setActionState] = useState(CLOSED);
  const [pendingState, setPendingState] = useState(CLOSED);
  const rights = {
    canModify: hasPermission(P.ACCOUNTS.PAYMENT_MODIFY),
    canRefund: hasPermission(P.ACCOUNTS.PAYMENT_REFUND),
    canDelete: hasPermission(P.ACCOUNTS.PAYMENT_DELETE),
  };

  const refresh = () => invalidateModuleFinance(queryClient, moduleType, moduleId, clientId);
  const resetForm = (draft = null, attachments = []) =>
    setFormSeed((prev) => ({ nonce: prev.nonce + 1, draft, attachments }));

  const addMutation = useMutation({
    mutationFn: (payload) => addModulePayment(moduleType, moduleId, payload),
    onSuccess: (_, payload) => {
      toast.success(
        payload.autoVerify ? "Payment recorded and verified" : "Payment submitted for verification"
      );
      resetForm();
      refresh();
      onPaymentSubmitted?.();
    },
    onError: (err) => toast.error(getApiError(err, "Failed to submit payment")),
  });

  const breakdown = chargeSummary(charges);
  const totalPaid = useMemo(() => totalVerifiedPaid(payments), [payments]);

  return (
    <Stack spacing={3}>
      <ModuleInvoicePanel moduleType={moduleType} moduleId={moduleId} clientId={clientId} />
      <ChargesSummaryCard
        charges={breakdown}
        primaryLabel={PRIMARY_CHARGE_LABELS[moduleType]}
        totalPaid={totalPaid}
        balanceDue={balanceDue(breakdown.finalAmount, totalPaid)}
      />

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
          Record payment
        </Typography>
        <RecordPaymentForm
          key={formSeed.nonce}
          initialDraft={formSeed.draft}
          initialAttachments={formSeed.attachments}
          requireMethod={requiresPaymentMethod(moduleType)}
          canSelfVerify={hasPermission(P.ACCOUNTS.PAYMENT_VERIFY)}
          submitting={addMutation.isLoading}
          onSubmit={(payload) => addMutation.mutate(payload)}
        />
      </Paper>

      <Stack spacing={1.5}>
        {isLoading || payments.length === 0 ? (
          <Typography variant="body2" color="text.secondary" className="py-6 text-center">
            {isLoading ? "Loading payments…" : "No payments recorded yet."}
          </Typography>
        ) : (
          payments.map((payment) => (
            <PaymentRow
              key={payment.id}
              payment={payment}
              rights={rights}
              onEditPending={(p) =>
                setPendingState({ ...CLOSED, open: true, mode: "edit", payment: p })
              }
              onWithdraw={(p) =>
                setPendingState({ ...CLOSED, open: true, mode: "withdraw", payment: p })
              }
              onResubmit={(p) =>
                setPendingState({ ...CLOSED, open: true, mode: "resubmit", payment: p })
              }
              onAction={(action, p) =>
                setActionState({ ...CLOSED, open: true, action, payment: p })
              }
            />
          ))
        )}
      </Stack>

      <PendingModulePaymentDialog
        open={pendingState.open}
        mode={pendingState.mode}
        payment={pendingState.payment}
        requireMethod={requiresPaymentMethod(moduleType)}
        onClose={() => setPendingState(CLOSED)}
        onChanged={() => {
          refresh();
          onPaymentSubmitted?.();
        }}
        onResubmit={(payment, payload) =>
          resubmitModulePayment(moduleType, moduleId, payment.id, payload)
        }
        onRecordNew={({ attachments, ...values }) =>
          resetForm({ ...values, method: values.method || "" }, attachments || [])
        }
      />
      <ModulePaymentActionModal
        open={actionState.open}
        action={actionState.action}
        payment={actionState.payment}
        refundableAvailable={refundableAvailable(payments)}
        onSuccess={refresh}
        onClose={() => setActionState(CLOSED)}
      />
    </Stack>
  );
};

export default ModulePaymentsTab;
